import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { InvoiceSheetShell } from '../../components/payment/InvoiceSheetShell.js';
import { useAuth } from '../../store/auth.js';
import { apiFetch } from '../../lib/api-client.js';
import { useStarWallet } from '../../hooks/useStarWallet.js';
import { topupBonus, topupBonusPercent, TOPUP_QUICK_PICKS, TOPUP_DEFAULT_PICK } from '@wds/astro-bot-shared/catalog';

interface ChatTopUpSheetProps {
  onDismiss: () => void;
  priceStars?: number;
  /** Called instead of onDismiss after a paid invoice. */
  onPaid?: () => void;
}

export function ChatTopUpSheet({ onDismiss, onPaid, priceStars }: ChatTopUpSheetProps): JSX.Element {
  const { t } = useTranslation(['home', 'course']);
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const [amount, setAmount] = useState(TOPUP_DEFAULT_PICK);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  // 50★ on the user's first top-up (0 after it) — the server decides, this only shows it.
  const { firstTopupBonus } = useStarWallet();
  const bonus = topupBonus(amount) + firstTopupBonus;

  async function handlePay(): Promise<void> {
    if (amount < 50 || amount > 100000) {
      setError(t('chat.topup.error'));
      return;
    }
    if (!jwt) { setError(t('chat.topup.error')); return; }
    setPaying(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/stars/topup-invoice', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ stars: amount }),
      });
      if (!res.ok) {
        setError(t('chat.topup.error'));
        return;
      }
      const data = (await res.json()) as { invoiceLink: string };
      const openInvoice = window.Telegram?.WebApp?.openInvoice;
      if (openInvoice) {
        openInvoice(data.invoiceLink, (status: string) => {
          if (status === 'paid') {
            void qc.invalidateQueries({ queryKey: ['star-wallet'] });
            (onPaid ?? onDismiss)();
          }
        });
      }
    } catch {
      setError(t('chat.topup.error'));
    } finally {
      setPaying(false);
    }
  }

  // Reuse the app-wide payment chrome (same sheet as every SKU purchase). The
  // top-up is a variable amount (no fixed SKU / no coupon backend for wallet
  // top-ups), so the amount picker rides in the shell's children slot and the
  // live amount drives the invoice price + CTA.
  return (
    <InvoiceSheetShell
      skuCode="stars_topup"
      title={t('chat.topup.title')}
      priceStars={amount}
      bullets={[]}
      glyph="★"
      ctaLabel={paying ? t('chat.topup.paying', { defaultValue: 'Открываю оплату…' }) : t('chat.topup.pay')}
      onPrimary={() => void handlePay()}
      primaryDisabled={paying}
      onCancel={onDismiss}
      testId="stars-topup-sheet"
      payTestId="stars-topup-pay"
      cancelTestId="stars-topup-cancel"
    >
      {priceStars !== undefined && <p>{t('course:chat.price', { price: priceStars })}</p>}
      {/* Amount picker */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {TOPUP_QUICK_PICKS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setAmount(v)}
            style={{
              flex: 1,
              minHeight: 40,
              padding: '4px 0',
              borderRadius: 10,
              border: amount === v ? '1px solid rgba(212,175,55,0.7)' : '0.5px solid rgba(255,220,160,0.16)',
              background: amount === v ? 'rgba(212,175,55,0.14)' : 'rgba(13,8,36,0.6)',
              color: 'var(--cream-hi, #f5e6d3)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {v} ★
            {topupBonusPercent(v) > 0 && (
              <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(212,175,55,0.95)' }}>
                +{topupBonusPercent(v)}%
              </span>
            )}
          </button>
        ))}
      </div>
      <label style={{ display: 'block', margin: '12px 0 4px', fontSize: 12.5, color: 'rgba(245,230,211,0.6)' }}>
        {t('chat.topup.amountLabel')}
      </label>
      <input
        type="number"
        min={50}
        max={100000}
        value={amount}
        onChange={(e) => setAmount(Number(e.target.value))}
        style={{
          width: '100%',
          height: 44,
          borderRadius: 10,
          border: '1px solid rgba(212,175,55,0.4)',
          background: 'rgba(255,255,255,0.05)',
          color: 'var(--cream-hi, #f5e6d3)',
          padding: '0 12px',
          fontSize: 16,
          boxSizing: 'border-box',
        }}
      />
      <p style={{ margin: '6px 0 0', fontSize: 12, color: 'rgba(245,230,211,0.5)' }}>
        {t('chat.topup.rangeHint')}
      </p>
      {firstTopupBonus > 0 && (
        <p data-testid="stars-topup-first-bonus" style={{ margin: '6px 0 0', fontSize: 12.5, fontWeight: 600, color: 'rgba(212,175,55,0.95)' }}>
          {t('chat.topup.firstBonus', { bonus: firstTopupBonus })}
        </p>
      )}
      <p data-testid="stars-topup-bonus" style={{ margin: '6px 0 0', fontSize: 12.5, color: 'rgba(212,175,55,0.95)' }}>
        {bonus > 0
          ? t('chat.topup.bonusGet', { total: amount + bonus, bonus })
          : t('chat.topup.bonusHint')}
      </p>
      {error && (
        <p role="alert" style={{ margin: '8px 0 0', color: 'var(--error-soft)', fontSize: 13 }}>
          {error}
        </p>
      )}
    </InvoiceSheetShell>
  );
}
